import { getTotalUnpaidPaymentsAmount, obfuscateName } from './EnrollmentHelper'
import { Enrollment, PayFor, PaymentMethod } from '../types/GrooverTypes'
import { ObfuscatedGroupMember } from '../types/QueryParamTypes'

/**
 * Payment methods that can be offered for "Pay for both" (V4 grouped couples,
 * combined into a single transaction/action covering both enrollments).
 *
 * Two different reasons a method qualifies:
 *  - Automated providers (Stripe, UserCredit, Epassi, Swish, VippsMobile) each
 *    have a `payForGroup*` cloud function that charges the combined total in
 *    one provider transaction and records one Payment per enrollment with
 *    correct userId/amount attribution (split at intent-creation time, fanned
 *    out on settlement).
 *  - Cash ("pay on site") and SwishManual ("Swish senare" — pay to the
 *    client's static Swish company number) have no automated settlement at
 *    all in the single-enrollment flow either — completing registration is a
 *    pure local UI action (no cloud function call, no per-enrollment
 *    reference), and staff always reconcile them manually afterwards via
 *    `RegisterManualPaymentRow` in admin-web. Grouping changes nothing about
 *    that: staff simply see two enrollments to collect from instead of one.
 *    (Note both are already treated as one bucket elsewhere, e.g.
 *    `hasSwishManualOrCash` in `ProcessHandlerRegistration.ts`.)
 *
 * BankTransfer is deliberately EXCLUDED: its instructions show a combined
 * amount but a single enrollment's OCR reference, and the automated SEB bank
 * reconciliation (`ProcessHandlerSeb.ts`) matches a transfer to exactly one
 * enrollment and posts the full amount to it — enabling it as-is would silently
 * overpay one sibling while leaving the other unpaid. Revisit only alongside a
 * reconciliation-side fix (see COU-210 discussion). Regular Swish (`Swish`) is
 * unrelated to this exclusion — it's the automated app-push flow with its own
 * `payForGroup*` callable, listed above.
 */
export const GROUP_PAY_FOR_BOTH_PAYMENT_METHODS: ReadonlySet<PaymentMethod> = new Set([
  PaymentMethod.Stripe,
  PaymentMethod.UserCredit,
  PaymentMethod.Epassi,
  PaymentMethod.Swish,
  PaymentMethod.VippsMobile,
  PaymentMethod.Cash,
  PaymentMethod.SwishManual
])

export type EnrollmentPaymentView =
  | { kind: 'single'; focusedEnrollment: Enrollment }
  | {
      kind: 'legacy-combined'
      focusedEnrollment: Enrollment
      showPayForRadios: boolean
    }
  | {
      kind: 'grouped'
      enrollmentGroupId: string
      focusedEnrollment: Enrollment
      members: ObfuscatedGroupMember[]
      showPayForGroup: boolean
      groupUnpaidTotal: number
    }

export type ResolveEnrollmentPaymentViewParams = {
  focusedEnrollment: Enrollment
  members?: ObfuscatedGroupMember[]
  allowPayForBoth?: boolean
  hardCoupleIndividualParticipantFlow?: boolean
}

export const buildObfuscatedGroupMembers = (siblings: Enrollment[]): ObfuscatedGroupMember[] =>
  siblings.map((enrollment) => ({
    enrollmentId: enrollment.id,
    userId: enrollment.mainUser.id,
    displayName: obfuscateName(enrollment.mainUser.name),
    unpaidAmount: getTotalUnpaidPaymentsAmount(enrollment, PayFor.Both),
    paymentStatus: enrollment.paymentStatus
  }))

export const buildObfuscatedGroupMemberFromEnrollment = (enrollment: Enrollment): ObfuscatedGroupMember => {
  const nameForDisplay = enrollment.mainUser.nonObfuscatedName ?? enrollment.mainUser.name

  return {
    enrollmentId: enrollment.id,
    userId: enrollment.mainUser.id,
    displayName: obfuscateName(nameForDisplay),
    unpaidAmount: getTotalUnpaidPaymentsAmount(enrollment, PayFor.Both),
    paymentStatus: enrollment.paymentStatus
  }
}

export const computeGroupUnpaidTotal = (members: ObfuscatedGroupMember[]): number =>
  members.reduce((sum, member) => sum + member.unpaidAmount, 0)

export const getGroupToBePaidData = (
  members: ObfuscatedGroupMember[],
  focusedEnrollmentId: string
): Record<PayFor, number> => {
  const mainMember = members.find((m) => m.enrollmentId === focusedEnrollmentId)
  const partnerMember = members.find((m) => m.enrollmentId !== focusedEnrollmentId)
  const mainAmount = mainMember?.unpaidAmount ?? 0
  const partnerAmount = partnerMember?.unpaidAmount ?? 0
  return {
    [PayFor.Main]: mainAmount,
    [PayFor.Partner]: partnerAmount,
    [PayFor.Both]: mainAmount + partnerAmount
  }
}

export const resolveGroupPaymentEnrollmentId = (
  members: ObfuscatedGroupMember[],
  focusedEnrollmentId: string,
  payFor: PayFor
): string => {
  if (payFor === PayFor.Partner) {
    return members.find((m) => m.enrollmentId !== focusedEnrollmentId)?.enrollmentId ?? focusedEnrollmentId
  }
  return focusedEnrollmentId
}

export const resolveEnrollmentPaymentView = ({
  focusedEnrollment,
  members = [],
  allowPayForBoth = false,
  hardCoupleIndividualParticipantFlow = false
}: ResolveEnrollmentPaymentViewParams): EnrollmentPaymentView => {
  if (focusedEnrollment.enrollmentGroupId) {
    const resolvedMembers = members.length > 0 ? members : [buildObfuscatedGroupMemberFromEnrollment(focusedEnrollment)]
    return {
      kind: 'grouped',
      enrollmentGroupId: focusedEnrollment.enrollmentGroupId,
      focusedEnrollment,
      members: resolvedMembers,
      showPayForGroup: allowPayForBoth === true,
      groupUnpaidTotal: computeGroupUnpaidTotal(resolvedMembers)
    }
  }

  if (focusedEnrollment.partnerUser) {
    return {
      kind: 'legacy-combined',
      focusedEnrollment,
      showPayForRadios: !hardCoupleIndividualParticipantFlow
    }
  }

  return {
    kind: 'single',
    focusedEnrollment
  }
}
